import { Button } from "../../components/adminDashboard/ResultsComponents";
import React, {useState} from "react"
import { MdClose } from "react-icons/md"
import { MdArrowBack, MdArrowForward } from "react-icons/md"
import { useHistory } from "react-router-dom";
import ContinousAssessment from "../../components/adminDashboard/ContinousAssessment";
import RemarksAndMore from "../../components/adminDashboard/RemarksAndMore";
import GeneralCustomization from "../../components/adminDashboard/GeneralCustomization";

const ranges = Array.from({ length: 101 },(_, i) => i);
const defaultPrimaryGrades = [
  {grade: "A*", min_score: 90, max_score: 100},
  {grade: "A", min_score: 80, max_score: 89},
  {grade: "B", min_score: 70, max_score: 79},
  {grade: "C", min_score: 60, max_score: 69},
  {grade: "D", min_score: 50, max_score: 59},
  {grade: "E", min_score: 40, max_score: 49},
  {grade: "F", min_score: 30, max_score: 39},
  {grade: "G", min_score: 20, max_score: 29}
];
const defaultSecondaryGrades = [
  {grade: "A1", min_score: 75, max_score: 100},
  {grade: "B2", min_score: 70, max_score: 74},
  {grade: "B3", min_score: 65, max_score: 69},
  {grade: "C4", min_score: 60, max_score: 64},
  {grade: "C5", min_score: 55, max_score: 59},
  {grade: "C6", min_score: 50, max_score: 54},
  {grade: "D7", min_score: 45, max_score: 49},
  {grade: "D8", min_score: 40, max_score: 44},
  {grade: "F9", min_score: 0, max_score: 39}
];
const sections = ["Continous Assessment", "Grading System", "Remarks, abilities, & trait reporting", "General Customization"]

const Setup = ({onClose}) => {
  const history = useHistory()
  const [page, setPage] = useState(0)
  const [error, setError] = useState(false)
  
  // State structure matching the expected payload
  const [formData, setFormData] = useState({
    assessment: {
      ca: {
        cas: [],
        max_score: 40
      },
      exam: {
        max_score: 60
      }
    },
    grading: {
      grading_system: [
        {
          level_of_education: "primary",
          grades: defaultPrimaryGrades,
          is_default: true
        }
      ]
    },
    remark: {
      teacher_remark: true,
      overall_comment: true,
      skills: [],
      ratings: [],
      remarks: []
    },
    result: {
      teacher_preview: true,
      admin_approval: false,
      parent_payment_threshold: 50,
      is_result_released: true
    },
    term_terminology: "Term"
  });

  const [selectedGradingIndex, setSelectedGradingIndex] = useState(0);

  const pages = [0, 1, 2, 3]

  // Handler for CA updates
  const handleCAUpdate = (updatedCA) => {
    setFormData(prev => ({
      ...prev,
      assessment: {
        ...prev.assessment,
        ca: updatedCA,
        exam: {
          max_score: 100 - (updatedCA.max_score || 0)
        }
      }
    }));
  };

  // Handler for term terminology updates
  const handleTermTerminologyUpdate = (newTerminology) => {
    setFormData(prev => ({
      ...prev,
      term_terminology: newTerminology
    }));
  };

  // Handler for grading system updates
  const handleGradingUpdate = (gradeIndex, field, value) => {
    setFormData(prev => {
      const updatedGradingSystem = prev.grading.grading_system.map((system, idx) => {
        if (idx !== selectedGradingIndex) return system;
        return {
          ...system,
          grades: system.grades.map((grade, index) =>
            index === gradeIndex
              ? { ...grade, [field]: parseInt(value) || 0 }
              : grade
          )
        };
      });
      return {
        ...prev,
        grading: {
          ...prev.grading,
          grading_system: updatedGradingSystem
        }
      };
    });
  };

  // Handler for remarks and more updates
  const handleRemarksUpdate = (updatedRemarks) => {
    setFormData(prev => ({
      ...prev,
      remark: updatedRemarks
    }));
  };

  // Handler for general customization updates
  const handleCustomizationUpdate = (field, value) => {
    setFormData(prev => ({
      ...prev,
      result: {
        ...prev.result,
        [field]: value
      }
    }));
  };

  const handleProceed = () => {
    // Transform remark object to match backend schema
    const { remark } = formData;
    const normalizedRemark = {
      remarks: Array.isArray(remark.remarksList) ? remark.remarksList : [],
      skills: Array.isArray(remark.skillsList) ? remark.skillsList : [],
      ratings: Array.isArray(remark.abilitiesList) ? remark.abilitiesList : [],
      teacher_remark: !!remark.remarks,
      overall_comment: !!remark.comments
    };
    const payload = {
      ...formData,
      remark: normalizedRemark
    };
    localStorage.setItem("summary", JSON.stringify(payload));
    history.push("/result-management/summary");
  };

  const handleAddGradingSystem = (level) => {
    setFormData(prev => {
      // Prevent duplicate levels
      if (prev.grading.grading_system.some(g => g.level_of_education === level)) return prev;
      const newGrades = level === "Primary" ? defaultPrimaryGrades : defaultSecondaryGrades;
      return {
        ...prev,
        grading: {
          ...prev.grading,
          grading_system: [
            ...prev.grading.grading_system,
            {
              level_of_education: level,
              grades: newGrades,
              is_default: false
            }
          ]
        }
      };
    });
  };

  const handleRemoveGradingSystem = (index) => {
    setFormData(prev => {
      const updated = prev.grading.grading_system.filter((_, idx) => idx !== index);
      return {
        ...prev,
        grading: {
          ...prev.grading,
          grading_system: updated.length ? updated : prev.grading.grading_system
        }
      };
    });
    setSelectedGradingIndex(0);
  };

  const handleSetDefaultGradingSystem = (index) => {
    setFormData(prev => {
      const updated = prev.grading.grading_system.map((g, idx) => ({
        ...g,
        is_default: idx === index
      }));
      return {
        ...prev,
        grading: {
          ...prev.grading,
          grading_system: updated
        }
      };
    });
  };

  return(
    <div className="fixed top-0 left-0 p-3 flex items-center justify-center min-h-screen h-full w-full bg-[#34405480] backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[85%] sm:max-h-[90%] bg-white mt-12 md:mt-0 px-5 py-6 rounded-3xl overflow-y-scroll">
        <div className="flex justify-between items-center gap-2">
          <p className="text-lg font-extrabold tracking-tight text-slate-900 dark:text-gray-200">
            Setup Result Metrics
          </p>

          <button onClick={onClose}><MdClose size={24}/></button>
        </div>

        <div className="my-6">
          <p className="text-sm text-gray-400">{sections[page]}</p>
          <div className="flex gap-1">
            {pages.map(currentPage => (
              <div 
                key={currentPage}
                className={`min-w-[50px] ${currentPage === page ? "w-44 bg-blue-800" : "bg-gray-400"} h-[2px] rounded`}
              />
            ))}
          </div>
        </div>

        {page === 0 && (
          <ContinousAssessment 
            ca={formData.assessment.ca}
            setCa={handleCAUpdate}
            termTerminology={formData.term_terminology}
            setTermTerminology={handleTermTerminologyUpdate}
            error={error} 
            setError={setError}
          />
        )}

        {page === 1 && (
          <div>
            <div className="flex gap-2 mb-4 capitalize">
              {["primary", "secondary"].map(level => (
                <button
                  key={level}
                  className={`px-3 py-1 rounded-full border capitalize ${formData.grading.grading_system.some(g => g.level_of_education === level) ? 'bg-blue-100 border-blue-500' : 'bg-gray-100 border-gray-300'}`}
                  onClick={() => handleAddGradingSystem(level)}
                  disabled={formData.grading.grading_system.some(g => g.level_of_education === level)}
                >
                  Add {level} Grading
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              {formData.grading.grading_system.map((system, idx) => (
                <button
                  key={system.level_of_education}
                  className={`px-3 py-1 rounded-full border capitalize ${selectedGradingIndex === idx ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                  onClick={() => setSelectedGradingIndex(idx)}
                >
                  {system.level_of_education}
                  {system.is_default && <span className="ml-2 text-xs">(Default)</span>}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mb-4">
              {formData.grading.grading_system.length > 1 && formData.grading.grading_system.map((system, idx) => (
                <button
                  key={system.level_of_education + '-remove'}
                  className="px-2 py-1 text-xs text-red-600 border border-red-300 rounded capitalize"
                  onClick={() => handleRemoveGradingSystem(idx)}
                >
                  Remove {system.level_of_education}
                </button>
              ))}
              {formData.grading.grading_system.length > 1 && formData.grading.grading_system.map((system, idx) => (
                <button
                  key={system.level_of_education + '-default'}
                  className="px-2 py-1 text-xs text-green-600 border border-green-300 rounded capitalize"
                  onClick={() => handleSetDefaultGradingSystem(idx)}
                  disabled={system.is_default}
                >
                  Set {system.level_of_education} as Default
                </button>
              ))}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 md:gap-3">
              {formData.grading.grading_system[selectedGradingIndex]?.grades.map((grade, index) => (
                <GradeMetrics
                  key={grade.grade}
                  grade={grade}
                  index={index}
                  onUpdate={handleGradingUpdate}
                />
              ))}
            </div>
          </div>
        )}

        {page === 2 && (
          <RemarksAndMore 
            remarkData={formData.remark}
            setFinalState={handleRemarksUpdate}
          />
        )}

        {page === 3 && (
          <GeneralCustomization 
            resultData={formData.result}
            onUpdate={handleCustomizationUpdate}
          />
        )}

        <div className="flex mt-3 justify-between gap-2">
          <Button 
            onClick={() => setPage(page-1)}
            disabled={page === pages[0]} 
            variant={2}
          >
            <MdArrowBack/> Back
          </Button>
          
          {page === 3 ? (
            <Button onClick={handleProceed}>
              Proceed to Summary
            </Button>
          ) : (
            <Button 
              onClick={() => setPage(page+1)}
              disabled={page === pages[pages.length - 1] || error}
            >
              Proceed <MdArrowForward/>
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

const GradeMetrics = ({grade, index, onUpdate}) => {
  const updateMinScore = (e) => {
    const score = parseInt(e.target.value ?? "0")
    onUpdate(index, 'min_score', score);
  }
  
  const updateMaxScore = (e) => {
    const score = parseInt(e.target.value ?? "0")
    onUpdate(index, 'max_score', score);
  }

  return(
    <div className="flex flex-wrap gap-3 items-start justify-between mx-auto max-w-sm w-full p-4 border rounded-3xl">
      <p className="text-base font-semibold">{grade.grade}</p>
      <div>
        <p>Weight</p>
        <div className="flex justify-between gap-2 items-center">
          <div className="rounded-sm overflow-hidden border">
            <select value={grade.min_score} onChange={updateMinScore} className="block border-none">
              <optgroup className="max-h-[60px]">
                {ranges.map(num => <option key={num} value={num}>{num}</option>)}
              </optgroup>
            </select>
          </div>

          <p>-</p>

          <div className="rounded-sm overflow-hidden border">
            <select onChange={updateMaxScore} value={grade.max_score} className="border-none">
              <optgroup className="max-h-[60px]">
                {ranges.map(num => <option key={num} value={num}>{num}</option>)}
              </optgroup>
            </select>
          </div>
        </div>
      </div>
    </div>
  )
}

export {Setup}